import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Search,
  Plus,
  Trash2,
  X,
  Check,
  AlertCircle,
  Link as LinkIcon,
  Target,
  Ban,
  TrendingUp,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play
} from 'lucide-react';
import { campaignService, CampaignKeyword } from '../../services/campaignService';
import { keywordService } from '../../services/keywordService';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';

interface CampaignKeywordsProps {
  campaignId: string;
  type: 'positive' | 'negative';
  campaignStatus?: string; // 'active' | 'paused' | 'draft' | etc — controla bloqueio do "Add"
}

const CampaignKeywords: React.FC<CampaignKeywordsProps> = ({ campaignId, type, campaignStatus }) => {
  const { t } = useTranslation();
  const [keywords, setKeywords] = useState<CampaignKeyword[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  // Se status não foi passado (componente usado em modo edit/wizard), assume ativo
  // pra não bloquear injustamente. Bloqueio efetivo só quando status é conhecido E !=='active'.
  const isCampaignActive = !campaignStatus || campaignStatus.toLowerCase() === 'active';

  const MATCH_TYPE_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    exact: { label: t('campaigns.campaignKeywords.exact', 'Exact'), color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: <Target className="w-3 h-3" /> },
    phrase: { label: t('campaigns.campaignKeywords.phrase', 'Phrase'), color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: <span className="text-xs">"..."</span> },
    broad: { label: t('campaigns.campaignKeywords.broad', 'Broad'), color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: <TrendingUp className="w-3 h-3" /> }
  };

  // Add keyword modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newKeywords, setNewKeywords] = useState('');
  const [matchType, setMatchType] = useState<'exact' | 'phrase' | 'broad' | 'auto'>('broad');
  const [isAdding, setIsAdding] = useState(false);

  // Parse a line with Google Ads syntax: [keyword]=exact, "keyword"=phrase, keyword=broad
  const parseKeywordLine = (line: string): { keyword: string; matchType: 'exact' | 'phrase' | 'broad' } => {
    const trimmed = line.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']') && trimmed.length > 2) {
      return { keyword: trimmed.slice(1, -1).trim(), matchType: 'exact' };
    }
    if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
      return { keyword: trimmed.slice(1, -1).trim(), matchType: 'phrase' };
    }
    return { keyword: trimmed, matchType: 'broad' };
  };

  // Link existing keywords modal
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [existingKeywords, setExistingKeywords] = useState<any[]>([]);
  const [selectedExisting, setSelectedExisting] = useState<Set<string>>(new Set());
  const [searchExisting, setSearchExisting] = useState('');
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  // Add to library inside link modal
  const [newLibraryKeywords, setNewLibraryKeywords] = useState('');
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);

  // Search & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Filter keywords by search
  const filteredKeywords = useMemo(() => {
    if (!searchQuery.trim()) return keywords;
    const q = searchQuery.toLowerCase();
    return keywords.filter(kw => kw.keyword.toLowerCase().includes(q));
  }, [keywords, searchQuery]);

  // Calculate paginated keywords
  const totalPages = Math.ceil(filteredKeywords.length / itemsPerPage);
  const paginatedKeywords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredKeywords.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredKeywords, currentPage, itemsPerPage]);


  // Reset to page 1 when keywords or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [keywords.length, searchQuery]);

  useEffect(() => {
    loadKeywords();
  }, [campaignId, type]);

  const loadKeywords = async () => {
    setIsLoading(true);
    try {
      const response = await campaignService.getCampaignKeywords(
        campaignId,
        type === 'negative' ? 'negative' : 'positive'
      );
      if (response.success) {
        setKeywords(response.data || []);
      }
    } catch (error) {
      console.error('Error loading keywords:', error);
      toast.error(t('campaigns.campaignKeywords.errorLoading', 'Error loading keywords'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadExistingKeywords = async () => {
    setIsLoadingExisting(true);
    try {
      const response = await keywordService.getKeywords({ limit: 100, global_only: 'true' });
      if (response.success) {
        setExistingKeywords(response.data || []);
      }
    } catch (error) {
      console.error('Error loading existing keywords:', error);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  const handleAddKeywords = async () => {
    if (!newKeywords.trim()) return;

    const rawLines = newKeywords
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (rawLines.length === 0) return;

    setIsAdding(true);
    try {
      if (matchType === 'auto') {
        const parsed = rawLines.map(parseKeywordLine).filter(p => p.keyword.length > 0);
        if (type === 'negative') {
          await campaignService.addNegativeKeywords(campaignId, parsed);
        } else {
          await campaignService.addKeywordsToCampaign(campaignId, parsed);
        }
        toast.success(`${parsed.length} ${t('campaigns.campaignKeywords.keywordsAdded', 'keyword(s) added')}`);
      } else {
        if (type === 'negative') {
          await campaignService.addNegativeKeywords(campaignId, rawLines, matchType);
        } else {
          await campaignService.addKeywordsToCampaign(campaignId, rawLines, matchType);
        }
        toast.success(`${rawLines.length} ${t('campaigns.campaignKeywords.keywordsAdded', 'keyword(s) added')}`);
      }
      setNewKeywords('');
      setIsAddModalOpen(false);
      loadKeywords();
    } catch (error: any) {
      const msg = error?.response?.data?.message || t('campaigns.campaignKeywords.errorAdding', 'Error adding keywords');
      toast.error(msg);
    } finally {
      setIsAdding(false);
    }
  };

  const handleLinkKeywords = async () => {
    if (selectedExisting.size === 0) return;

    setIsLinking(true);
    try {
      await campaignService.linkExistingKeywords(
        campaignId,
        Array.from(selectedExisting),
        matchType === 'auto' ? 'broad' : matchType
      );
      toast.success(`${selectedExisting.size} ${t('campaigns.campaignKeywords.keywordsLinked', 'keyword(s) linked')}`);
      setSelectedExisting(new Set());
      setIsLinkModalOpen(false);
      loadKeywords();
    } catch (error) {
      toast.error(t('campaigns.campaignKeywords.errorLinking', 'Error linking keywords'));
    } finally {
      setIsLinking(false);
    }
  };

  const handleRemoveKeyword = async (keywordId: string) => {
    try {
      await campaignService.removeKeywordFromCampaign(campaignId, keywordId);
      toast.success(t('campaigns.campaignKeywords.keywordRemoved', 'Keyword removed'));
      setKeywords(prev => prev.filter(k => k.id !== keywordId));
      selectedKeywords.delete(keywordId);
      setSelectedKeywords(new Set(selectedKeywords));
    } catch (error) {
      toast.error(t('campaigns.campaignKeywords.errorRemoving', 'Error removing keyword'));
    }
  };

  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const handleBulkRemove = async () => {
    if (selectedKeywords.size === 0 || isBulkRemoving) return;

    const ids = Array.from(selectedKeywords);
    const confirmed = window.confirm(t('campaigns.campaignKeywords.confirmRemove', 'Remove {{count}} keyword(s)?', { count: ids.length }));
    if (!confirmed) return;

    setIsBulkRemoving(true);
    setBulkProgress({ done: 0, total: ids.length });

    // Run deletions in parallel with a concurrency cap to avoid flooding the backend
    const CONCURRENCY = 5;
    let doneCount = 0;
    const failures: string[] = [];
    const successfulIds = new Set<string>();

    const runOne = async (keywordId: string) => {
      try {
        await campaignService.removeKeywordFromCampaign(campaignId, keywordId);
        successfulIds.add(keywordId);
      } catch (err: any) {
        failures.push(err?.response?.data?.message || err?.message || keywordId);
      } finally {
        doneCount++;
        setBulkProgress({ done: doneCount, total: ids.length });
      }
    };

    // Simple worker-pool pattern with fixed concurrency
    let cursor = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, ids.length) }, async () => {
      while (cursor < ids.length) {
        const idx = cursor++;
        await runOne(ids[idx]);
      }
    });
    await Promise.all(workers);

    if (successfulIds.size > 0) {
      setKeywords(prev => prev.filter(k => !successfulIds.has(k.id)));
      toast.success(`${successfulIds.size} ${t('campaigns.campaignKeywords.keywordsRemoved', 'keyword(s) removed')}`);
    }
    if (failures.length > 0) {
      toast.error(
        t('campaigns.campaignKeywords.errorRemovingBulkPartial', '{{n}} erro(s) ao remover: {{msg}}', {
          n: failures.length,
          msg: failures[0].substring(0, 80)
        })
      );
    }
    setSelectedKeywords(new Set());
    setIsBulkRemoving(false);
    setBulkProgress({ done: 0, total: 0 });
  };

  // Pausar / ativar uma palavra-chave positiva (no Google é o status do critério).
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const isKeywordPaused = (kw: any) => String(kw.status || '').toLowerCase() === 'paused';
  const handleToggleKeywordStatus = async (kw: any) => {
    const next = isKeywordPaused(kw) ? 'active' : 'paused';
    setTogglingIds(prev => new Set(prev).add(kw.id));
    try {
      const res = await campaignService.updateCampaignKeyword(campaignId, kw.id, { status: next });
      if (res && (res as any).success === false) {
        toast.error((res as any).message || t('campaigns.rmf.kwStatusError', 'Falha ao mudar o status da palavra'));
      } else {
        toast.success(next === 'paused'
          ? t('campaigns.rmf.kwPaused', 'Palavra-chave pausada')
          : t('campaigns.rmf.kwEnabled', 'Palavra-chave ativada'));
      }
      await loadKeywords();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('campaigns.rmf.kwStatusError', 'Falha ao mudar o status da palavra'));
    } finally {
      setTogglingIds(prev => {
        const n = new Set(prev);
        n.delete(kw.id);
        return n;
      });
    }
  };

  const handleUpdateMatchType = async (keywordId: string, newMatchType: string) => {
    try {
      await campaignService.updateCampaignKeyword(campaignId, keywordId, { matchType: newMatchType });
      setKeywords(prev => prev.map(k =>
        k.id === keywordId ? { ...k, match_type: newMatchType as any } : k
      ));
      toast.success(t('campaigns.campaignKeywords.matchTypeUpdated', 'Match type updated'));
    } catch (error) {
      toast.error(t('campaigns.campaignKeywords.errorUpdating', 'Error updating'));
    }
  };

  const handleAddToLibrary = async () => {
    if (!newLibraryKeywords.trim()) return;

    const keywordList = newLibraryKeywords
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (keywordList.length === 0) return;

    setIsAddingToLibrary(true);
    try {
      await keywordService.createBulkKeywords(keywordList);
      toast.success(`${keywordList.length} ${t('campaigns.campaignKeywords.addedToLibrary', 'keyword(s) added to library')}`);
      setNewLibraryKeywords('');
      loadExistingKeywords();
    } catch (error) {
      toast.error(t('campaigns.campaignKeywords.errorAddingToLibrary', 'Error adding to library'));
    } finally {
      setIsAddingToLibrary(false);
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedKeywords);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedKeywords(newSelected);
  };

  const toggleSelectAll = () => {
    const currentPageIds = paginatedKeywords.map(k => k.id);
    const allCurrentPageSelected = currentPageIds.every(id => selectedKeywords.has(id));

    if (allCurrentPageSelected) {
      const newSelected = new Set(selectedKeywords);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedKeywords(newSelected);
    } else {
      const newSelected = new Set(selectedKeywords);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedKeywords(newSelected);
    }
  };

  const selectAllKeywords = () => {
    setSelectedKeywords(new Set(keywords.map(k => k.id)));
  };

  const deselectAllKeywords = () => {
    setSelectedKeywords(new Set());
  };

  const filteredExisting = existingKeywords.filter(k =>
    k.keyword.toLowerCase().includes(searchExisting.toLowerCase())
  );

  const formatVolume = (volume?: number) => {
    if (!volume) return '-';
    if (volume >= 1000) return `${(volume / 1000).toFixed(1)}K`;
    return volume.toString();
  };

  return (
    <div className="space-y-4 relative">
      {/* Bulk remove overlay — sticky banner at top with progress */}
      {isBulkRemoving && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl px-5 py-3 flex items-center gap-3 min-w-[280px]">
          <Loader2 className="w-5 h-5 animate-spin text-red-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {t('campaigns.campaignKeywords.bulkRemoving', 'Removendo palavras-chave...')}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-500 transition-all duration-200"
                  style={{ width: bulkProgress.total > 0 ? `${(bulkProgress.done / bulkProgress.total) * 100}%` : '0%' }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono flex-shrink-0">
                {bulkProgress.done}/{bulkProgress.total}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {type === 'negative' ? (
            <Ban className="w-5 h-5 text-red-500" />
          ) : (
            <Target className="w-5 h-5 text-green-500" />
          )}
          <h3 className="font-medium text-gray-900 dark:text-white">
            {type === 'negative'
              ? t('campaigns.campaignKeywords.negativeKeywords', 'Negative Keywords')
              : t('campaigns.campaignKeywords.keywords', 'Keywords')}
          </h3>
          <span className="text-sm text-gray-500">({keywords.length})</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('campaigns.campaignKeywords.searchKeywords', 'Buscar...')}
              className="pl-8 pr-3 py-1.5 w-44 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          {selectedKeywords.size > 0 && (
            <Button variant="danger" size="sm" onClick={handleBulkRemove} disabled={isBulkRemoving}>
              {isBulkRemoving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              {isBulkRemoving
                ? `${bulkProgress.done}/${bulkProgress.total}`
                : `${t('campaigns.campaignKeywords.remove', 'Remove')} (${selectedKeywords.size})`}
            </Button>
          )}

          {type === 'positive' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setIsLinkModalOpen(true);
                loadExistingKeywords();
              }}
              disabled={!isCampaignActive}
              title={!isCampaignActive ? t('campaigns.campaignKeywords.activateFirst', 'Ative a campanha pra adicionar keywords') : ''}
            >
              <LinkIcon className="w-4 h-4 mr-1" />
              {t('campaigns.campaignKeywords.linkExisting', 'Link Existing')}
            </Button>
          )}

          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            disabled={!isCampaignActive}
            title={!isCampaignActive ? t('campaigns.campaignKeywords.activateFirst', 'Ative a campanha pra adicionar keywords') : ''}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('campaigns.campaignKeywords.add', 'Add')}
          </Button>

          <Button variant="ghost" size="sm" onClick={loadKeywords}>
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Keywords List */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('campaigns.campaignKeywords.loadingKeywords', 'Carregando keywords...')}
          </p>
        </div>
      ) : keywords.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400 mb-3">
            {type === 'negative'
              ? t('campaigns.campaignKeywords.noNegativeKeywords', 'No negative keywords added')
              : t('campaigns.campaignKeywords.noKeywordsAdded', 'No keywords added to campaign')}
          </p>
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            disabled={!isCampaignActive}
            title={!isCampaignActive ? t('campaigns.campaignKeywords.activateFirst', 'Ative a campanha pra adicionar keywords') : ''}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('campaigns.campaignKeywords.addKeywords', 'Add Keywords')}
          </Button>
          {!isCampaignActive && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center justify-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              {t('campaigns.campaignKeywords.activateFirstHint', 'Ative a campanha primeiro pra poder adicionar keywords')}
            </p>
          )}
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {/* Positivas têm muitas colunas: rolagem horizontal no celular */}
          <div className="overflow-x-auto">
          <div className={type === 'positive' ? 'min-w-[1200px]' : ''}>
          {/* Table Header */}
          <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center gap-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            <div className="w-8">
              <input
                type="checkbox"
                checked={paginatedKeywords.length > 0 && paginatedKeywords.every(k => selectedKeywords.has(k.id))}
                onChange={toggleSelectAll}
                className="rounded border-gray-300 text-primary-600"
              />
            </div>
            <div className="flex-1 min-w-[140px]">{t('campaigns.campaignKeywords.keyword', 'Keyword')}</div>
            <div className="w-20 text-center">{t('campaigns.campaignKeywords.type', 'Type')}</div>
            {type === 'positive' && (
              <>
                <div className="w-20 text-center">{t('campaigns.rmf.kwStatus', 'Status')}</div>
                <div className="w-14 text-right">CPC</div>
                <div className="w-20 text-right pl-2">{t('campaigns.campaignKeywords.impressions', 'Impressões')}</div>
                <div className="w-16 text-right pl-2">{t('campaigns.campaignKeywords.clicks', 'Cliques')}</div>
                <div className="w-14 text-right">CTR</div>
                <div className="w-16 text-right">{t('campaigns.campaignKeywords.cost', 'Gasto')}</div>
                <div className="w-16 text-right">{t('campaigns.campaignKeywords.conversions', 'Conv.')}</div>
                <div className="w-16 text-right">{t('campaigns.campaignKeywords.costPerConv', 'Custo/Conv')}</div>
                <div className="w-20 text-right normal-case" title={t('campaigns.rmf.cpcFirstPageHint', 'Estimativa do Google para aparecer na 1ª página')}>{t('campaigns.rmf.cpcFirstPage', 'CPC 1ª página')}</div>
                <div className="w-20 text-right normal-case" title={t('campaigns.rmf.cpcTopHint', 'Estimativa do Google para aparecer no topo da página')}>{t('campaigns.rmf.cpcTop', 'CPC topo da página')}</div>
                <div className="w-20 text-right normal-case" title={t('campaigns.rmf.cpcFirstPosHint', 'Estimativa do Google para aparecer na 1ª posição')}>{t('campaigns.rmf.cpcFirstPos', 'CPC 1ª posição')}</div>
              </>
            )}
            <div className={type === 'positive' ? 'w-20' : 'w-10'}></div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedKeywords.map((kw: any) => {
              const fmtMoney = (v: any) => (v && Number(v) > 0) ? `R$${Number(v).toFixed(2)}` : '-';
              const fmtInt = (v: any) => (v && Number(v) > 0) ? Number(v).toLocaleString('pt-BR') : '0';
              const fmtPct = (v: any) => (v && Number(v) > 0) ? `${(Number(v) * 100).toFixed(2)}%` : '-';
              // Estimativas de CPC do Google (null = sem estimativa)
              const est = kw.cpcEstimates || kw.cpc_estimates || {};
              const fmtEst = (v: any) => (v == null || isNaN(Number(v))) ? '—' : `R$${Number(v).toFixed(2)}`;
              const paused = isKeywordPaused(kw);
              return (
              <div
                key={kw.id}
                className={`px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                  selectedKeywords.has(kw.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                }`}
              >
                <div className="w-8">
                  <input
                    type="checkbox"
                    checked={selectedKeywords.has(kw.id)}
                    onChange={() => toggleSelect(kw.id)}
                    className="rounded border-gray-300 text-primary-600"
                  />
                </div>

                <div className="flex-1 min-w-[140px]">
                  <span className="text-gray-900 dark:text-white font-medium">{kw.keyword}</span>
                </div>

                <div className="w-20">
                  <select
                    value={kw.match_type}
                    onChange={(e) => handleUpdateMatchType(kw.id, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${MATCH_TYPE_LABELS[kw.match_type]?.color || 'bg-gray-100'}`}
                  >
                    <option value="exact">{t('campaigns.campaignKeywords.exact', 'Exact')}</option>
                    <option value="phrase">{t('campaigns.campaignKeywords.phrase', 'Phrase')}</option>
                    <option value="broad">{t('campaigns.campaignKeywords.broad', 'Broad')}</option>
                  </select>
                </div>

                {type === 'positive' && (
                  <>
                    <div className="w-20 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${paused
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'}`}>
                        {paused ? t('campaigns.rmf.kwStatusPaused', 'Pausada') : t('campaigns.rmf.kwStatusActive', 'Ativa')}
                      </span>
                    </div>
                    <div className="w-14 text-right text-sm text-gray-600 dark:text-gray-400">
                      {fmtMoney(kw.cpc)}
                    </div>
                    <div className="w-20 text-right text-sm text-gray-700 dark:text-gray-300">
                      {fmtInt(kw.impressions)}
                    </div>
                    <div className="w-16 text-right text-sm text-gray-700 dark:text-gray-300">
                      {fmtInt(kw.clicks)}
                    </div>
                    <div className="w-14 text-right text-sm text-gray-700 dark:text-gray-300">
                      {fmtPct(kw.ctr)}
                    </div>
                    <div className="w-16 text-right text-sm text-gray-700 dark:text-gray-300">
                      {fmtMoney(kw.cost)}
                    </div>
                    <div className="w-16 text-right text-sm text-gray-700 dark:text-gray-300">
                      {(kw.conversions && Number(kw.conversions) > 0) ? Number(kw.conversions).toFixed(1) : '0'}
                    </div>
                    <div className="w-16 text-right text-sm text-gray-700 dark:text-gray-300">
                      {fmtMoney(kw.cost_per_conversion)}
                    </div>
                    <div className="w-20 text-right text-sm text-gray-700 dark:text-gray-300">{fmtEst(est.firstPage)}</div>
                    <div className="w-20 text-right text-sm text-gray-700 dark:text-gray-300">{fmtEst(est.topOfPage)}</div>
                    <div className="w-20 text-right text-sm text-gray-700 dark:text-gray-300">{fmtEst(est.firstPosition)}</div>
                  </>
                )}

                <div className={`${type === 'positive' ? 'w-20' : 'w-10'} flex items-center justify-end gap-1`}>
                  {type === 'positive' && (
                    <button
                      onClick={() => handleToggleKeywordStatus(kw)}
                      disabled={togglingIds.has(kw.id)}
                      className={`p-1.5 rounded transition-colors disabled:opacity-50 ${paused
                        ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30'
                        : 'text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'}`}
                      title={paused ? t('campaigns.rmf.kwEnable', 'Ativar palavra-chave') : t('campaigns.rmf.kwPause', 'Pausar palavra-chave')}
                      aria-label={paused ? t('campaigns.rmf.kwEnable', 'Ativar palavra-chave') : t('campaigns.rmf.kwPause', 'Pausar palavra-chave')}
                    >
                      {togglingIds.has(kw.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveKeyword(kw.id)}
                    className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
          </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('campaigns.campaignKeywords.showing', 'Showing')} {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredKeywords.length)} {t('campaigns.campaignKeywords.of', 'of')} {filteredKeywords.length}{searchQuery && ` (${keywords.length} total)`}
                </span>
                {selectedKeywords.size > 0 && (
                  <span className="text-sm text-primary-600 dark:text-primary-400">
                    ({selectedKeywords.size} {t('campaigns.campaignKeywords.selected', 'selected')})
                  </span>
                )}
                {keywords.length > itemsPerPage && (
                  <button
                    onClick={selectedKeywords.size === keywords.length ? deselectAllKeywords : selectAllKeywords}
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    {selectedKeywords.size === keywords.length
                      ? t('campaigns.campaignKeywords.deselectAll', 'Deselect all')
                      : t('campaigns.campaignKeywords.selectAll', 'Select all')}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      if (page === 1 || page === totalPages) return true;
                      if (Math.abs(page - currentPage) <= 1) return true;
                      return false;
                    })
                    .reduce((acc: (number | 'ellipsis')[], page, index, arr) => {
                      if (index > 0 && page - (arr[index - 1] as number) > 1) {
                        acc.push('ellipsis');
                      }
                      acc.push(page);
                      return acc;
                    }, [])
                    .map((item, index) =>
                      item === 'ellipsis' ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-400">...</span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => setCurrentPage(item)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === item
                              ? 'bg-primary-500 text-white'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {item}
                        </button>
                      )
                    )
                  }
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Keywords Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title={type === 'negative'
          ? t('campaigns.campaignKeywords.addNegativeKeywords', 'Add Negative Keywords')
          : t('campaigns.campaignKeywords.addKeywords', 'Add Keywords')}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('campaigns.campaignKeywords.keywordsPerLine', 'Keywords (one per line)')}
            </label>
            <textarea
              value={newKeywords}
              onChange={(e) => setNewKeywords(e.target.value)}
              placeholder={matchType === 'auto'
                ? (type === 'negative'
                    ? 'free\n"cheap shoes"\n[download free]'
                    : 'digital marketing\n"social media management"\n[marketing automation]')
                : (type === 'negative'
                    ? "free\ncheap\ndownload free\nhow to diy"
                    : "digital marketing\nsocial media management\nmarketing automation")}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              {newKeywords.split('\n').filter(k => k.trim()).length} {t('campaigns.campaignKeywords.keyword', 'keyword').toLowerCase()}(s)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('campaigns.campaignKeywords.matchType', 'Match Type')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(['broad', 'phrase', 'exact', 'auto'] as const).map((mt) => (
                <button
                  key={mt}
                  onClick={() => setMatchType(mt)}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    matchType === mt
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {mt === 'auto'
                    ? t('campaigns.campaignKeywords.auto', 'Auto')
                    : MATCH_TYPE_LABELS[mt].label}
                </button>
              ))}
            </div>
            {matchType === 'auto' ? (
              <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-800 dark:text-blue-200 space-y-1">
                <p className="font-medium">{t('campaigns.campaignKeywords.autoDescTitle', 'O tipo é detectado por linha conforme a sintaxe do Google Ads:')}</p>
                <p>
                  <code className="px-1 py-0.5 bg-white dark:bg-gray-800 rounded">palavra</code>
                  {' → '}
                  {t('campaigns.campaignKeywords.broadShort', 'Ampla')}
                </p>
                <p>
                  <code className="px-1 py-0.5 bg-white dark:bg-gray-800 rounded">"palavra chave"</code>
                  {' → '}
                  {t('campaigns.campaignKeywords.phraseShort', 'Frase')}
                </p>
                <p>
                  <code className="px-1 py-0.5 bg-white dark:bg-gray-800 rounded">[palavra exata]</code>
                  {' → '}
                  {t('campaigns.campaignKeywords.exactShort', 'Exata')}
                </p>
              </div>
            ) : (
              <p className="mt-2 text-xs text-gray-500">
                {matchType === 'exact' && t('campaigns.campaignKeywords.exactDescFull', 'Exata [palavra]: anúncios aparecem apenas para buscas idênticas ao termo.')}
                {matchType === 'phrase' && t('campaigns.campaignKeywords.phraseDescFull', 'Frase "palavra": anúncios aparecem em buscas que contenham a frase exata.')}
                {matchType === 'broad' && t('campaigns.campaignKeywords.broadDescFull', 'Ampla: anúncios aparecem em buscas relacionadas, incluindo sinônimos e variações.')}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsAddModalOpen(false)}>
              {t('campaigns.campaignKeywords.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleAddKeywords} isLoading={isAdding}>
              {t('campaigns.campaignKeywords.add', 'Add')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Library & Link Keywords Modal */}
      <Modal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        title={t('campaigns.campaignKeywords.library', 'Keyword Library')}
        size="2xl"
      >
        <div className="space-y-4">
          {/* Add to library section */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('campaigns.campaignKeywords.addToLibrary', 'Add new to library (one per line)')}
            </label>
            <textarea
              value={newLibraryKeywords}
              onChange={(e) => setNewLibraryKeywords(e.target.value)}
              placeholder={"marketing digital\nseo local\ngoogle ads"}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <Button
              size="sm"
              onClick={handleAddToLibrary}
              isLoading={isAddingToLibrary}
              disabled={!newLibraryKeywords.trim()}
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('campaigns.campaignKeywords.saveToLibrary', 'Save to Library')}
            </Button>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700" />

          {/* Search and link existing */}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('campaigns.campaignKeywords.selectFromLibrary', 'Select keywords from the library to link to this campaign:')}
          </p>

          <Input
            placeholder={t('campaigns.campaignKeywords.searchKeywords', 'Search keywords...')}
            value={searchExisting}
            onChange={(e) => setSearchExisting(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('campaigns.campaignKeywords.matchType', 'Match Type')}
            </label>
            <div className="flex gap-2">
              {(['broad', 'phrase', 'exact'] as const).map((mt) => (
                <button
                  key={mt}
                  onClick={() => setMatchType(mt)}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    matchType === mt
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {MATCH_TYPE_LABELS[mt].label}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            {isLoadingExisting ? (
              <div className="p-4 text-center text-gray-500">{t('campaigns.campaignKeywords.loading', 'Loading...')}</div>
            ) : filteredExisting.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchExisting
                  ? t('campaigns.campaignKeywords.noKeywordsFound', 'No keywords found')
                  : t('campaigns.campaignKeywords.libraryEmpty', 'Library is empty. Add keywords above.')}
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredExisting.map((kw) => (
                  <label
                    key={kw.id}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${
                      selectedExisting.has(kw.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedExisting.has(kw.id)}
                      onChange={() => {
                        const newSelected = new Set(selectedExisting);
                        if (newSelected.has(kw.id)) {
                          newSelected.delete(kw.id);
                        } else {
                          newSelected.add(kw.id);
                        }
                        setSelectedExisting(newSelected);
                      }}
                      className="rounded border-gray-300 text-primary-600"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 dark:text-white">{kw.keyword}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatVolume(kw.search_volume)}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {selectedExisting.size > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {selectedExisting.size} {t('campaigns.campaignKeywords.keywordsSelected', 'keyword(s) selected')}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsLinkModalOpen(false)}>
              {t('campaigns.campaignKeywords.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleLinkKeywords}
              isLoading={isLinking}
              disabled={selectedExisting.size === 0}
            >
              {t('campaigns.campaignKeywords.link', 'Link')} ({selectedExisting.size})
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CampaignKeywords;
